import { useEffect, useRef, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { apiFetch } from "../lib/api";
import { uploadPhoto } from "../lib/upload";
import { uploadVoiceNote } from "../lib/voiceNotes";
import { GroupPicker } from "./GroupPicker";
import { PlaceTypeahead } from "./PlaceTypeahead";
import { ScanOverlay, type ScannedPhoto } from "./scan/ScanOverlay";
import type { Recording } from "./scan/VoiceRecorder";
import type { CreatePostRequest, FinalizeMediaRequest, PlaceSuggestion, Post } from "@family/shared";

interface NewPostFormProps {
  familySlug: string;
}

interface QueuedFile {
  id: string; // client-side tmp id
  file: File;
  previewUrl: string;
  status: "queued" | "uploading" | "done" | "error";
  progress: number;
  mediaId?: string;
  error?: string;
  /** Set for a photo from the scanner: saved with source "scan", and the
   *  unenhanced crop (if any) is uploaded first as its original. */
  scan?: { original: File | null; voiceNote: Recording | null };
  /** The photo saved but its spoken story didn't; the post can still go out. */
  voiceError?: string;
}

function todayIso(): string {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export function NewPostForm({ familySlug }: NewPostFormProps) {
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [eventDate, setEventDate] = useState<string>(todayIso());
  const [visibility, setVisibility] = useState<"everyone" | "groups">("everyone");
  const [groupIds, setGroupIds] = useState<string[]>([]);
  const [files, setFiles] = useState<QueuedFile[]>([]);
  // Optional location tag (place-autocomplete). Only sent when a suggestion
  // is picked, since the World map needs real coordinates.
  const [placeText, setPlaceText] = useState("");
  const [pickedPlace, setPickedPlace] = useState<PlaceSuggestion | null>(null);
  const [scanning, setScanning] = useState(false);

  // Header greeting suggestion "Share a memory…" navigates here and fires
  // this event: expand the composer, scroll it into view and focus the
  // title so the user lands ready to type.
  useEffect(() => {
    const handler = () => {
      setOpen(true);
      // Defer so the expanded form has rendered (#post-title exists).
      setTimeout(() => {
        const el = document.getElementById("post-title");
        el?.scrollIntoView({ behavior: "smooth", block: "center" });
        (el as HTMLInputElement | null)?.focus();
      }, 60);
    };
    window.addEventListener("compose:focus", handler);
    return () => window.removeEventListener("compose:focus", handler);
  }, []);

  const create = useMutation<Post, Error, CreatePostRequest>({
    mutationFn: (payload) =>
      apiFetch<Post>(`/families/${familySlug}/posts`, {
        method: "POST",
        body: JSON.stringify(payload),
      }),
    onSuccess: () => {
      setTitle("");
      setBody("");
      setEventDate(todayIso());
      setPlaceText("");
      setPickedPlace(null);
      for (const f of files) URL.revokeObjectURL(f.previewUrl);
      setFiles([]);
      setOpen(false);
      void queryClient.invalidateQueries({ queryKey: ["posts", familySlug] });
      void queryClient.invalidateQueries({ queryKey: ["locations", familySlug] });
    },
  });

  function reset() {
    for (const f of files) URL.revokeObjectURL(f.previewUrl);
    setFiles([]);
    setTitle("");
    setBody("");
    setEventDate(todayIso());
    setPlaceText("");
    setPickedPlace(null);
    setVisibility("everyone");
    setGroupIds([]);
    setOpen(false);
  }

  function onPickFiles(e: React.ChangeEvent<HTMLInputElement>) {
    const picked = Array.from(e.target.files ?? []);
    e.target.value = ""; // allow re-picking the same file
    addFiles(picked);
  }

  function addScan(photo: ScannedPhoto) {
    setScanning(false);
    addFiles([photo.file], { original: photo.original, voiceNote: photo.voiceNote });
  }

  function addFiles(picked: File[], scan?: QueuedFile["scan"]) {
    const next: QueuedFile[] = picked.map((file) => ({
      id: crypto.randomUUID(),
      file,
      previewUrl: URL.createObjectURL(file),
      status: "queued",
      progress: 0,
      ...(scan ? { scan } : {}),
    }));
    setFiles((prev) => [...prev, ...next]);
    void uploadQueued(next);
  }

  async function uploadQueued(queue: QueuedFile[]) {
    for (const q of queue) {
      setFiles((prev) =>
        prev.map((p) =>
          p.id === q.id ? { ...p, status: "uploading" } : p,
        ),
      );
      try {
        let finalize: Omit<FinalizeMediaRequest, "mimeType" | "width" | "height"> | undefined;
        if (q.scan) {
          finalize = { source: "scan" };
          if (q.scan.original) {
            const originalMediaId = await uploadPhoto(familySlug, q.scan.original, {
              finalize: { source: "scan" },
            });
            finalize = { source: "scan", originalMediaId };
          }
        }
        const mediaId = await uploadPhoto(familySlug, q.file, {
          finalize,
          onProgress: (pct) =>
            setFiles((prev) =>
              prev.map((p) => (p.id === q.id ? { ...p, progress: pct } : p)),
            ),
        });
        let voiceError: string | undefined;
        if (q.scan?.voiceNote) {
          try {
            await uploadVoiceNote(familySlug, mediaId, q.scan.voiceNote.blob, q.scan.voiceNote.durationMs);
          } catch {
            voiceError = "The recording couldn't be saved.";
          }
        }
        setFiles((prev) =>
          prev.map((p) =>
            p.id === q.id
              ? { ...p, status: "done", progress: 100, mediaId, ...(voiceError ? { voiceError } : {}) }
              : p,
          ),
        );
      } catch (err) {
        setFiles((prev) =>
          prev.map((p) =>
            p.id === q.id
              ? {
                  ...p,
                  status: "error",
                  error: err instanceof Error ? err.message : String(err),
                }
              : p,
          ),
        );
      }
    }
  }

  function removeFile(id: string) {
    setFiles((prev) => {
      const f = prev.find((x) => x.id === id);
      if (f) URL.revokeObjectURL(f.previewUrl);
      return prev.filter((x) => x.id !== id);
    });
  }

  if (!open) {
    return (
      <button onClick={() => setOpen(true)} style={{ alignSelf: "flex-start" }}>
        + New post
      </button>
    );
  }

  const uploading = files.some((f) => f.status === "uploading");
  const doneFiles = files.filter((f) => f.status === "done");
  const readyToPost = body.trim().length > 0 && !uploading && 
    (visibility === "everyone" || groupIds.length > 0);

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!readyToPost) return;
    create.mutate({
      title: title.trim() || undefined,
      body: body.trim(),
      eventDate: eventDate || undefined,
      mediaIds: doneFiles.map((f) => f.mediaId!).filter(Boolean),
      visibility,
      groupIds: visibility === "groups" ? groupIds : undefined,
      ...(pickedPlace
        ? {
            placeName: pickedPlace.displayName,
            placeLat: pickedPlace.lat,
            placeLng: pickedPlace.lng,
          }
        : {}),
    });
  }

  return (
    <form onSubmit={handleSubmit} className="card stack">
      <h3 style={{ fontSize: "1rem", margin: 0 }}>New post</h3>

      <div>
        <label htmlFor="post-title">Title (optional)</label>
        <input
          id="post-title"
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          disabled={create.isPending}
          maxLength={200}
        />
      </div>

      <div>
        <label htmlFor="post-body">What happened?</label>
        <p className="text-subtle" style={{ fontSize: "0.8rem", marginBottom: 4 }}>
          Describe this moment. Tell us who is in the photo/video and what is happening.
          This helps family members who weren't there feel included.
        </p>
        <textarea
          id="post-body"
          value={body}
          onChange={(e) => setBody(e.target.value)}
          disabled={create.isPending}
          required
          rows={5}
          style={{
            width: "100%",
            background: "var(--c-bg)",
            color: "var(--c-text)",
            border: "1px solid var(--c-border-strong)",
            borderRadius: 4,
            padding: "14px 16px",
            fontSize: "1rem",
            fontFamily: "inherit",
            resize: "vertical",
          }}
        />
      </div>

      <div>
        <label htmlFor="post-date">
          Pictured date (when the photo is from)
        </label>
        <p className="text-subtle" style={{ fontSize: "0.8rem", marginBottom: 4 }}>
          Today for news, or anything in the past for historical photos. You can be as
          precise as you like — try <code>1953</code>, <code>1953-04</code>, or
          <code>1953-04-07</code>.
        </p>
        <input
          id="post-date"
          type="text"
          inputMode="numeric"
          placeholder="YYYY, YYYY-MM, or YYYY-MM-DD"
          pattern="\d{4}(-\d{2}(-\d{2})?)?"
          value={eventDate}
          onChange={(e) => setEventDate(e.target.value)}
          disabled={create.isPending}
        />
      </div>

      <div>
        <label htmlFor="post-place">Location (optional)</label>
        <p className="text-subtle" style={{ fontSize: "0.8rem", marginBottom: 4 }}>
          Where was this? Pick a place to drop it on the family map.
        </p>
        <PlaceTypeahead
          value={placeText}
          onChange={setPlaceText}
          onPick={(place) => setPickedPlace(place)}
          hasPickedPlace={pickedPlace !== null}
          placeholder="City, place or landmark (start typing)"
          disabled={create.isPending}
        />
      </div>

      <GroupPicker
        familySlug={familySlug}
        visibility={visibility}
        selectedGroupIds={groupIds}
        onVisibilityChange={setVisibility}
        onGroupsChange={setGroupIds}
        disabled={create.isPending}
      />

      <div>
        <label>Photos & Videos</label>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp,image/gif,image/heic,image/heif,video/mp4,video/webm,video/ogg,video/quicktime,video/mpeg,video/x-msvideo,video/x-ms-wmv,video/x-matroska,video/3gpp,video/x-flv,video/mp2t,.mpg,.mpeg,.avi,.wmv,.mkv,.flv,.3gp,.ts,.mov"
          multiple
          onChange={onPickFiles}
          style={{ display: "none" }}
        />
        <button
          type="button"
          className="btn-secondary"
          onClick={() => fileInputRef.current?.click()}
          disabled={create.isPending}
          style={{ width: "100%" }}
        >
          Add photos or videos
        </button>
        <button
          type="button"
          className="btn-tertiary"
          onClick={() => setScanning(true)}
          disabled={create.isPending}
          style={{ width: "100%", marginTop: "var(--s-2)" }}
        >
          📷 Scan an old printed photo
        </button>

        {files.length > 0 && (
          <ul
            className="list-unstyled"
            style={{
              display: "grid",
              gridTemplateColumns:
                "repeat(auto-fill, minmax(100px, 1fr))",
              gap: "var(--s-2)",
              marginTop: "var(--s-3)",
            }}
          >
            {files.map((f) => (
              <li
                key={f.id}
                style={{
                  position: "relative",
                  aspectRatio: "1 / 1",
                  borderRadius: 4,
                  overflow: "hidden",
                  background: "var(--c-bg-alt)",
                }}
              >
                <img
                  src={f.previewUrl}
                  alt=""
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    display: "block",
                    opacity:
                      f.status === "uploading" || f.status === "error"
                        ? 0.5
                        : 1,
                  }}
                />
                {f.status === "uploading" && (
                  <div
                    style={{
                      position: "absolute",
                      inset: 0,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "#fff",
                      fontWeight: 700,
                      textShadow: "0 1px 2px rgba(0,0,0,0.8)",
                    }}
                  >
                    {f.progress}%
                  </div>
                )}
                {f.scan?.voiceNote && (
                  <span
                    title={f.voiceError ?? "Has a spoken story"}
                    aria-label={f.voiceError ?? "Has a spoken story"}
                    style={{
                      position: "absolute",
                      left: 4,
                      bottom: 4,
                      background: f.voiceError ? "var(--c-danger)" : "rgba(0,0,0,0.6)",
                      color: "#fff",
                      borderRadius: 12,
                      padding: "1px 6px",
                      fontSize: "0.75rem",
                    }}
                  >
                    🎙{f.voiceError ? " !" : ""}
                  </span>
                )}
                {f.status === "error" && (
                  <div
                    className="text-error"
                    style={{
                      position: "absolute",
                      inset: 0,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      textAlign: "center",
                      padding: "0.25rem",
                      fontSize: "0.7rem",
                      background: "rgba(255,255,255,0.85)",
                    }}
                  >
                    failed
                  </div>
                )}
                <button
                  type="button"
                  onClick={() => removeFile(f.id)}
                  aria-label="Remove photo"
                  style={{
                    position: "absolute",
                    top: 4,
                    right: 4,
                    background: "rgba(0,0,0,0.6)",
                    color: "#fff",
                    border: "none",
                    borderRadius: "50%",
                    width: 24,
                    height: 24,
                    minHeight: 24,
                    padding: 0,
                    fontSize: "0.9rem",
                    lineHeight: 1,
                    cursor: "pointer",
                  }}
                >
                  ×
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {files.some((f) => f.voiceError) && (
        <p className="text-error">
          A recording couldn't be saved. The photo is fine; you can post it now, or remove it and scan again.
        </p>
      )}
      {create.error && <p className="text-error">{create.error.message}</p>}

      {scanning && (
        <ScanOverlay acceptLabel="Add to post" onAccept={addScan} onCancel={() => setScanning(false)} />
      )}

      <div style={{ display: "flex", gap: "var(--s-3)" }}>
        <button type="submit" disabled={create.isPending || !readyToPost}>
          {create.isPending
            ? "Publishing…"
            : uploading
              ? "Uploading photos…"
              : "Publish"}
        </button>
        <button
          type="button"
          className="btn-secondary"
          onClick={reset}
          disabled={create.isPending}
        >
          Cancel
        </button>
      </div>
    </form>
  );
}
